import { X } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * A titled group of fields inside a dialog.
 *
 * Long forms are navigated by scanning, not reading: a rule and a caps heading every few fields
 * give the eye somewhere to land, which an undifferentiated column of inputs does not. Cheap
 * enough that any form past about six fields should use it.
 */
export function FormSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-ink-800/10 pb-1.5">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800/50">
          {title}
        </h4>
        {hint ? <span className="text-[10px] text-ink-800/40">{hint}</span> : null}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/**
 * A labelled control. `min-w-0` is on the label because these sit in grids and flex rows, where
 * a missing one turns a long value into horizontal overflow rather than a shrunk box.
 */
export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`block min-w-0 ${className ?? ''}`}>
      <span className="mb-1 block text-xs text-ink-800/60">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-[10px] text-ink-800/40">{hint}</span> : null}
    </label>
  );
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'max-w-md',
  fill = false,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  /**
   * Hands the body's height to the content instead of scrolling it.
   *
   * The default is right for a form: it grows, and the dialog scrolls once. It is wrong for a
   * screen that already has a scrolling region of its own — a long table under a fixed summary —
   * where the two end up as neighbouring scrollbars and neither one is obviously the one you
   * want. With this set the body becomes a flex column that never scrolls, and the content is
   * responsible for its own `flex-1 min-h-0 overflow-auto` region.
   */
  fill?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-6 print:hidden"
      role="dialog"
      aria-modal="true"
    >
      <div className={`flex max-h-[90vh] w-full ${width} flex-col overflow-hidden rounded-xl bg-white shadow-xl`}>
        <header className="flex items-start justify-between border-b border-ink-800/10 p-6">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink-950">{title}</h3>
            {subtitle ? <p className="mt-0.5 text-xs text-ink-800/60">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="size-5 text-ink-800/50 hover:text-ink-950" />
          </button>
        </header>
        <div
          className={
            fill
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-6'
              : 'flex-1 overflow-auto p-6'
          }
        >
          {children}
        </div>
        {footer ? <footer className="flex gap-2 border-t border-ink-800/10 p-6">{footer}</footer> : null}
      </div>
    </div>
  );
}

/**
 * Control styling with **no width of its own**, for anything sized by its call site.
 *
 * Two width utilities on one element resolve by stylesheet order, not class order, so
 * `${modalInput} w-16` silently keeps `w-full` and the control eats its whole row. Compose from
 * this instead whenever the width is not "all of it".
 */
export const modalControl =
  'rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500';

export const modalInput = `w-full ${modalControl}`;
export const btnGhost =
  'flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5';
export const btnPrimary =
  'flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50';
